/* eslint-disable @typescript-eslint/no-explicit-any */

interface RemoteTracker {
    error: (err: Error, context?: Record<string, any>) => void;
}

/**
 * Severity levels for logging
 */
export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
}

const IS_DEV = import.meta.env.DEV;

/**
 * Unified Logger Utility
 * Bridges local console logging with remote analytics/error tracking.
 */
class Logger {
    private level: LogLevel = IS_DEV ? LogLevel.DEBUG : LogLevel.INFO;
    private correlationId: string | null = null;
    private tracker: RemoteTracker | null = null;

    /**
     * Set a remote tracker for errors (breaks circular dependencies)
     */
    public setTracker(tracker: RemoteTracker) {
        this.tracker = tracker;
    }

    /**
     * Set the current log level
     */
    public setLevel(level: LogLevel) {
        this.level = level;
    }

    /**
     * Create a new logger instance with a correlation ID
     */
    public withContext(correlationId: string): Logger {
        const contextLogger = new Logger();
        contextLogger.correlationId = correlationId;
        contextLogger.level = this.level;
        contextLogger.tracker = this.tracker;
        return contextLogger;
    }

    private formatMessage(message: string): string {
        return this.correlationId
            ? `[${this.correlationId}] ${message}`
            : message;
    }

    private sendToTerminal(level: string, message: string, ...args: unknown[]) {
        if (IS_DEV && (import.meta as any).hot) {
            (import.meta as any).hot.send('terminal:log', {
                level,
                message: this.formatMessage(message),
                args
            });
        }
    }

    /**
     * Log debug information (only in DEV console)
     */
    public debug(message: string, ...args: unknown[]) {
        const formatted = this.formatMessage(message);
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.debug(`[DEBUG] ${formatted}`, ...args);
        }
    }

    /**
     * Log general information and track as analytics event
     */
    public info(message: string, ...args: unknown[]) {
        const formatted = this.formatMessage(message);
        if (this.shouldLog(LogLevel.INFO)) {
            // eslint-disable-next-line no-console
            console.log(`[INFO] ${formatted}`, ...args);
            this.sendToTerminal('INFO', message, ...args);
        }

        // Removed: We no longer pipe 'system_log' to Firebase Analytics 
        // to prevent technical noise from polluting behavioral tools.
    }

    /**
     * Log warnings and track as analytics event
     */
    public warn(message: string, ...args: unknown[]) {
        const formatted = this.formatMessage(message);
        if (this.shouldLog(LogLevel.WARN)) {
            console.warn(`[WARN] ${formatted}`, ...args);
            this.sendToTerminal('WARN', message, ...args);
        }

        // Removed: We no longer pipe 'validation_error' to Firebase Analytics 
        // to prevent technical noise from polluting behavioral tools.
    }

    /**
     * Log errors and track them to remote error tracking
     */
    public error(message: string, error?: Error | unknown, context?: Record<string, unknown>) {
        const formatted = this.formatMessage(message);
        if (this.shouldLog(LogLevel.ERROR)) {
            console.error(`[ERROR] ${formatted}`, error, context);
            this.sendToTerminal('ERROR', message, { error, ...context });
        }

        // Track to remote service via registered tracker
        let err: Error;
        let enhancedContext: Record<string, unknown> = { ...context, correlationId: this.correlationId, message };
        
        if (error instanceof Error) {
            err = error;
        } else {
            err = new Error(message);
            // Preserve the original error object properties if it's a non-Error object
            if (error && typeof error === 'object') {
                enhancedContext = { ...enhancedContext, original_error: error };
            } else if (error) {
                enhancedContext = { ...enhancedContext, original_error: String(error) };
            }
        }

        this.tracker?.error(err, enhancedContext);
    }

    private shouldLog(requestedLevel: LogLevel): boolean {
        return requestedLevel >= this.level;
    }
}

export const logger = new Logger();
export interface LogEntry {
    level: LogLevel;
    message: string;
    correlationId: string | null;
    timestamp: number;
    args?: unknown[];
    context?: Record<string, unknown>;
}
